/**
 * One logged contact.
 *
 * Layout lifted from `ContractorFileItem` in the main project: header row with icon + title on the
 * left and timestamp + author affordance on the right, then a nested bordered block for the
 * payload. Shared by the drawer's General tab (the latest one) and its External contacts tab (all
 * of them) so a contact looks the same in both places.
 */
import { memo } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { GSIcon, GSIconName } from 'src/components/shared/GSIcon'
import { colors } from 'src/theme'
import { contactChannelTitle, formatContactAge } from './LastContactCell'
import { ContactChannel } from './types'

interface Props {
  contact: {
    channel: ContactChannel
    daysAgo: number
    /** Who logged it. */
    by: string
    note: string | null
  }
}

const CHANNEL_ICON: Record<ContactChannel, GSIconName> = {
  call: 'gs_call',
  text: 'gs_bubble_annotation_4_2',
  note: 'gs_notepad_2',
}

const ContactHistoryItemComponent = ({ contact }: Props) => {
  const channelIconName = CHANNEL_ICON[contact.channel] || 'gs_notepad_2'

  return (
    <div
      css={tw`flex flex-col border border-solid border-[#EDEDED] py-3 bg-white px-4 rounded-2xl`}
    >
      <div css={tw`flex flex-row justify-between items-start`}>
        <div css={tw`flex flex-row items-start`}>
          <GSIcon iconName={channelIconName} width={20} height={20} color={colors.GRAY_BLACK} />
          <Typography variant="h5" css={tw`ml-2 max-w-[200px] break-words`}>
            {contactChannelTitle[contact.channel]}
          </Typography>
        </div>
        <div css={tw`flex flex-row items-center`}>
          <Typography variant="body2" color={colors.SHADES_GREY_500}>
            {formatContactAge(contact.daysAgo)}
          </Typography>
          <Tooltip
            color="black"
            placement="right"
            title={
              <div>
                <Typography css={tw`mb-2`} variant="h6" color={colors.SHADES_GREY_200}>
                  Logged by
                </Typography>
                <Typography variant="body2" color={colors.SHADES_GREY_300}>
                  {contact.by}
                </Typography>
              </div>
            }
          >
            <span css={tw`h-5 cursor-pointer ml-2`}>
              <GSIcon
                iconName="gs_people_circle"
                width={20}
                height={20}
                color={colors.SHADES_GREY_400}
              />
            </span>
          </Tooltip>
        </div>
      </div>
      <div
        css={tw`flex flex-row border border-solid border-[#EDEDED] py-3 bg-white px-4 rounded-2xl mt-4 items-center justify-between`}
      >
        <div>
          <Typography variant="body2" color={colors.SHADES_GREY_500}>
            Note
          </Typography>
          <Typography variant="h6">{contact.note || 'No note added'}</Typography>
        </div>
      </div>
    </div>
  )
}

export const ContactHistoryItem = memo(ContactHistoryItemComponent)
